import { CategoryItems } from "@/app/(shop)/services/[slug]/components/categoryItems";
import { RECOMMENDED_CATEGORY_ID } from "@/constants/itemMarkers";
import { Item, Layout } from '@/dto/item';
import { Markers } from "@/dto/marker";
import { Menu } from '@/dto/menu';
import { FEATURE_FLAG_ITEM_MARKER } from "@/utils/constants";
import { hasMerchantFeatureFlag } from "@/utils/merchantFeatureFlags";
import { getRecommendedMarker, getItemIdsForMarker, getMarkerDisplayName } from '@/utils/recommendedItemsCategoryUtils';
import { useMerchant } from "@/app/providers/merchantProvider";
import { Category } from "@/dto/category";
import { useRecommendedCategory } from "@/contexts/RecommendedCategoryContext";
import { useEffect, useMemo, useRef } from "react";

interface RecommendedItemsCategoryProps {
  readonly menu: Menu | null;
  readonly markers?: Markers;
  readonly itemTemplate?: string;
  readonly query: string | undefined;
  readonly baseRoute?: string;
  readonly queryParams?: string;
  readonly itemsMap: Record<string, Item> | null;
  readonly getItemHref?: (item: Item) => string;
  readonly layout?: Layout;
}

export const RecommendedItemsCategory = ({
  baseRoute,
  itemsMap,
  itemTemplate,
  markers,
  menu,
  query,
  queryParams,
  getItemHref,
  layout = "default",
}: RecommendedItemsCategoryProps) => {
  const { merchant } = useMerchant();

  const isItemMarkerEnabled = Boolean(
    merchant?.isRestaurant &&
      hasMerchantFeatureFlag(merchant, FEATURE_FLAG_ITEM_MARKER)
  );
  const recommendedMarker = useMemo(
    () => (isItemMarkerEnabled ? getRecommendedMarker(menu) : undefined),
    [isItemMarkerEnabled, menu]
  );
  const recommendedItemIds = useMemo(
    () =>
      recommendedMarker
        ? getItemIdsForMarker(menu, recommendedMarker.id)
        : [],
    [menu, recommendedMarker]
  );
  const recommendedCategory = useMemo<Category | undefined>(() => {
    if (!recommendedMarker || recommendedItemIds.length === 0) return undefined;
    return {
      id: RECOMMENDED_CATEGORY_ID,
      name: getMarkerDisplayName(recommendedMarker),
      items: recommendedItemIds,
      sortOrder: -1,
    };
  }, [recommendedItemIds, recommendedMarker]);
  const { setRecommendedCategory } = useRecommendedCategory();
  const publishedCategoryRef = useRef<Category | null>(null);

  useEffect(() => {
    const category = isItemMarkerEnabled ? recommendedCategory ?? null : null;
    publishedCategoryRef.current = category;
    setRecommendedCategory(category);
  }, [isItemMarkerEnabled, recommendedCategory, setRecommendedCategory]);

  useEffect(
    () => () => {
      const publishedCategory = publishedCategoryRef.current;
      setRecommendedCategory((currentCategory) =>
        currentCategory === publishedCategory ? null : currentCategory
      );
    },
    [setRecommendedCategory]
  );

  if (!isItemMarkerEnabled || !menu || !recommendedCategory) return null;

  return (
    <CategoryItems
      {...{
        category: recommendedCategory,
        merchantName: merchant.name,
        itemTemplate,
        itemsMap,
        query: query || '',
        getItemHref,
        baseRoute,
        queryParams,
        markers,
        layout
      }}
    />
  );
};